import api from './api'
import type {
  GoogleAuthInitResponse,
  GoogleConnectionStatusResponse,
  GoogleConnectionSuccessResponse,
  GoogleAuthCallbackRequest,
} from '@/schemas/google-integration'
import type {
  GoogleDriveFolderListResponse,
  GoogleDriveFilePreviewResponse,
} from '@/schemas/google-drive-sync'
import type { AxiosRequestConfig } from 'axios'

export const googleIntegrationService = {
  /**
   * Initiate Google OAuth flow
   * Returns the auth URL to redirect the user to Google consent screen
   */
  async initiateAuth(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<GoogleAuthInitResponse> {
    const response = await api.post<GoogleAuthInitResponse>(
      `/organizations/${orgId}/integrations/google/auth`,
      {},
      config
    )
    return response.data
  },

  /**
   * Handle Google OAuth callback
   * Exchanges the authorization code for tokens and creates the connection
   */
  async handleCallback(
    orgId: string,
    data: GoogleAuthCallbackRequest,
    config?: AxiosRequestConfig
  ): Promise<GoogleConnectionSuccessResponse> {
    const response = await api.post<GoogleConnectionSuccessResponse>(
      `/organizations/${orgId}/integrations/google/callback`,
      data,
      config
    )
    return response.data
  },

  /**
   * Get Google connection status for an organization
   */
  async getStatus(
    orgId: string,
    config?: AxiosRequestConfig
  ): Promise<GoogleConnectionStatusResponse> {
    const response = await api.get<GoogleConnectionStatusResponse>(
      `/organizations/${orgId}/integrations/google/status`,
      config
    )
    return response.data
  },

  /**
   * Disconnect Google account from organization
   */
  async disconnect(orgId: string, config?: AxiosRequestConfig): Promise<void> {
    await api.delete(`/organizations/${orgId}/integrations/google`, config)
  },

  /**
   * List folders in Google Drive
   * Used by the folder picker component
   */
  async listDriveFolders(
    orgId: string,
    parentId: string = 'root',
    config?: AxiosRequestConfig
  ): Promise<GoogleDriveFolderListResponse> {
    const response = await api.get<GoogleDriveFolderListResponse>(
      `/organizations/${orgId}/integrations/google/drive/folders`,
      {
        ...config,
        params: { parent_id: parentId },
      }
    )
    return response.data
  },

  /**
   * Preview files in a Google Drive folder
   * Shows files that will be synced based on filters
   */
  async previewFolderFiles(
    orgId: string,
    folderId: string,
    fileTypes: string[] = ['pdf', 'docx', 'txt', 'md'],
    config?: AxiosRequestConfig
  ): Promise<GoogleDriveFilePreviewResponse> {
    const response = await api.get<GoogleDriveFilePreviewResponse>(
      `/organizations/${orgId}/integrations/google/drive/folders/${folderId}/files`,
      {
        ...config,
        params: { file_types: fileTypes },
        paramsSerializer: {
          indexes: null, // This sends file_types=pdf&file_types=docx instead of file_types[0]=pdf
        },
      }
    )
    return response.data
  },
}
